// Ghosts (§2.12): appearance after the KO delay, flight confined to the map bounds, and pebbles
// (drop, telegraphed fall, impact). Pebble drops and impacts are server-only (§18.2); in predict
// mode a pebble only falls.
import { DT, NO_SLOT, TUNE } from '../constants';
import { EV, emit } from '../events';
import { BS, Cause, EK } from '../ids';
import { MAPS } from '../maps/index';
import { approachVec } from '../math';
import type { Bean, Entity, Vec2, World } from '../types';
import { nudgeBodiesMask } from './collision';
import { removeEntity, spawnEntity } from './entities';
import { isPredicting } from './world';

/** A ghost is playable once the BONK CAM delay after its KO has passed. */
export function ghostReady(b: Bean): boolean {
  return b.state === BS.GHOST && b.st >= TUNE.ghost.delayTicks;
}

function clampToBounds(w: World, b: Bean): void {
  const { minX, maxX, minY, maxY } = MAPS[w.cfg.mapId].bounds;
  const pad = TUNE.ghost.boundsPad;
  if (b.pos.x < minX - pad || b.pos.x > maxX + pad) {
    b.pos.x = Math.min(maxX + pad, Math.max(minX - pad, b.pos.x));
    b.vel.x = 0;
  }
  if (b.pos.y < minY - pad || b.pos.y > maxY + pad) {
    b.pos.y = Math.min(maxY + pad, Math.max(minY - pad, b.pos.y));
    b.vel.y = 0;
  }
}

/** Ghost flight: hidden until the delay ends (GHOST_READY), then hovering at ghost height. */
export function moveGhost(w: World, b: Bean, move: Vec2): void {
  const g = TUNE.ghost;
  if (b.st < g.delayTicks) return;
  if (b.st === g.delayTicks) {
    b.z = g.z;
    b.vz = 0;
    b.vel.x = b.vel.y = 0;
    clampToBounds(w, b);
    emit(w, EV.GHOST_READY, b.slot);
  }
  approachVec(b.vel, move.x * g.speed, move.y * g.speed, g.accel * DT);
  b.pos.x += b.vel.x * DT;
  b.pos.y += b.vel.y * DT;
  clampToBounds(w, b);
}

/** Input phase: Shove as a ready ghost drops a pebble from its position (server-only). */
export function ghostActions(w: World, b: Bean, shovePressed: boolean): void {
  if (!shovePressed || !ghostReady(b) || b.cdPebble > 0 || isPredicting(w)) return;
  const g = TUNE.ghost;
  const e = spawnEntity(w, EK.PEBBLE, b.slot, b.pos.x, b.pos.y, b.z);
  e.timer = g.pebbleFallTicks;
  e.data2 = b.slot;
  e.vz = -b.z / (g.pebbleFallTicks * DT);
  b.cdPebble = g.pebbleCooldownTicks;
  emit(w, EV.PEBBLE_DROP, b.slot, NO_SLOT, 0, 0, b.pos.x, b.pos.y);
}

/**
 * Entity phase: the pebble falls through its telegraph (`timer` ticks, counting its drop tick),
 * then lands on the next tick and nudges everything under it (§2.12).
 */
export function stepPebble(w: World, e: Entity): void {
  if (e.timer > 0) {
    e.timer--;
    e.z = e.timer > 0 ? Math.max(0, e.z + e.vz * DT) : 0;
    return;
  }
  if (isPredicting(w)) return;
  const g = TUNE.ghost;
  const ghost = e.data2;
  const mask = nudgeBodiesMask(w, e.pos.x, e.pos.y, g.pebbleRadius, g.pebbleKb, g.pebbleDizzy, Cause.PEBBLE, ghost, g.pebbleMaxZ);
  for (let slot = 0; mask >> slot; slot++) if (mask & (1 << slot)) emit(w, EV.PEBBLE_HIT, ghost, slot);
  removeEntity(w, e.id);
}
